import React, { useEffect, useState, useRef, CSSProperties } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { DropdownMenu } from "../shared";
import { RootState } from "../../redux/store";
import useFetchLogo from "../../hooks/useFetchLogo";
import useFormChangeHandler from "../../hooks/useFormChangeHandler";
import { setGetPath } from "../../redux/reducer/prompt/promptSlice";
import { setActiveTab } from "../../redux/reducer/activeTab/activeTabSlice";

import {
  AccountingIcon,
  AgentManagementIcon,
  ApiSidebarIcon,
  AuditLogSidebarIcon,
  ChangePasswordSidebarIcon,
  CustomerIcon,
  DashboardSidebarIcon,
  DoubleUserIcon,
  CommunicationsSidebarIcon,
  OrderManagementIcon,
  ProfileSidebarIcon,
  RepManagementIcon,
  ServiceConfigIcon,
  SettingSidebarIcon,
} from "../../assets";
import { setLocationData } from "../../redux/reducer/locationSlice/locationSlice";
import { SALES_REP_ROLE } from "../../utils/salesRepAccess";
import useExceptionGroup from "../../pages/communications/TicketQueue/hooks/useExceptionGroup";
import useViewedGroups from "../../pages/communications/TicketQueue/hooks/useViewedGroups";
import ViewAnotherGroupModal from "../../pages/communications/TicketQueue/components/ViewAnotherGroupModal";
import { groupRoute } from "../../pages/communications/TicketQueue/queueConstants";

const SidebarPlainItem: React.FC<{
  className: string;
  onClick: () => void;
  titleLabel: string;
  to: string;
  handleLinkNavigation: (path: string, e: React.MouseEvent<HTMLElement>) => void;
  children: React.ReactNode;
  isActive: boolean;
}> = ({ className, onClick, titleLabel, to, handleLinkNavigation, children, isActive }) => {
  const liRef = useRef<HTMLLIElement>(null);
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState<CSSProperties>({});
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showPopup = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (!liRef.current || !document.body.classList.contains("sidebar-hidden-desktop")) return;
    const liRect = liRef.current.getBoundingClientRect();
    setPopupStyle({ top: `${liRect.top}px` });
    setIsHoverOpen(true);
  };

  const hidePopup = () => {
    hideTimeoutRef.current = setTimeout(() => setIsHoverOpen(false), 100);
  };

  return (
    <li ref={liRef} className={className} onClick={onClick} onMouseEnter={showPopup} onMouseLeave={hidePopup}>
      {children}
      <ul
        className={`layoutSidemenu__list layoutSidemenu__list--submenu ${isHoverOpen ? "hover-open" : ""}`}
        style={isHoverOpen ? popupStyle : {}}
        onMouseEnter={() => { if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); }}
        onMouseLeave={hidePopup}
      >
        <li className={`layoutSidemenu__list--item layoutSidemenu__list--itemTitle ${isActive ? "layoutSidemenu__selected" : ""}`}>
          <Link
            className="layoutSidemenu__list--link"
            to={to}
            onClick={(e) => { setIsHoverOpen(false); handleLinkNavigation(to, e); }}
          >
            <span>{titleLabel}</span>
          </Link>
        </li>
      </ul>
    </li>
  );
};

const SideBar: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { userRole, isUserOfOmniMove, customerLevel, isCustomer } = useSelector(
    (state: RootState) => state.auth
  );
  const { version } = useSelector((state: RootState) => state.staticData);
  // Communications › Exceptions shows only while the Exception group exists (KAN-1347)
  const hasCommunications =
    !isUserOfOmniMove && customerLevel === "first" && userRole !== "agent" && userRole !== SALES_REP_ROLE;
  const exceptionGroup = useExceptionGroup(hasCommunications, location.pathname);
  // Communications › Groups (KAN-1362): Exceptions, then the groups the user picked under View Another Group
  const { viewedGroups, setGroups: setViewGroups } = useViewedGroups(hasCommunications, location.pathname);
  const [isViewGroupsOpen, setIsViewGroupsOpen] = useState(false);
  // Everyone at a first-level customer who works tickets.
  const communicationsRoles = isUserOfOmniMove
    ? []
    : customerLevel === "first"
      ? ["admin", "manager", "employee", "customersuperadmin"]
      : [];
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const { handleNavigation, isFormChanged } = useFormChangeHandler();
  const [loading, setLoading] = useState<boolean>(false);
  // Custom hook to fetch logo URL from API
  const { logoUrl } = useFetchLogo({
    apiUrl: "/customer/logo",
    setLoading,
  });

  /**
   * Route configuration object defining the links and paths for different
   * management sections (e.g., user management, customer management, etc.).
   */
  const routeConfig = {
    userManagement: {
      links: [{ to: "/users", label: "Site Users", roles: ["*"] }],
      paths: [
        {
          parent: "/users",
          paths: ["/users", "/users/add", /^\/users\/edit\/\d+$/],
        },
      ],
    },
    customerManagement: {
      links: [
        {
          to: "/customers",
          label: "Customers",
          roles: isUserOfOmniMove
            ? ["superadmin", "admin", "manager", "customersuperadmin"]
            : ["admin", "manager", "customersuperadmin", SALES_REP_ROLE] ||
            (customerLevel === "second" && [
              "admin",
              "manager",
              "customersuperadmin",
            ]) ||
            (customerLevel === "third" && ["customer", "employee"]),
        },
        {
          to: "/customer-users",
          label: "Customer Users",
          roles: isUserOfOmniMove
            ? []
            : ["admin", "manager", "customersuperadmin"],
        },
        {
          to: "/customer-items",
          label: "Customer Items",
          roles:
            !isUserOfOmniMove && customerLevel === "first"
              ? ["admin", "manager", "customersuperadmin"]
              : [],
        },
        {
          to: "/customer-address-book",
          label: "Address Book",
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first"
              ? ["admin", "manager", "employee", "customersuperadmin"]
              : [],
        },

        // {
        //   to: "/customer/rate",
        //   label: "Customer Rates",
        //   roles:
        //     customerLevel === "first"
        //       ? ["admin", "manager", "employee"]
        //       : customerLevel === "second"
        //       ? []
        //       : ["admin", "manager", "employee"],
        // },
        // {
        //   to: "/customer-markup",
        //   label: "Customer Markup",
        //   roles: isUserOfOmniMove
        //     ? []
        //     : customerLevel === "second"
        //     ? [] // Hide for "second" level customers
        //     : customerLevel === "third"
        //     ? ["customer", "employee"]
        //     : ["admin", "manager"],
        // },
      ],
      paths: [
        {
          parent: "/customers",
          paths: [
            "/customers",
            "/customers/add",
            /^\/customers\/edit\/\d+$/,
            "/customer-address",
          ],
        },
        {
          parent: "/customer-users",
          paths: [
            "/customer-users",
            "/customer-users/add",
            /^\/customer-users\/edit\/\d+$/,
          ],
        },
        {
          parent: "/customer-items",
          paths: [
            "/customer-items",
            "/customer-items/add",
            /^\/customer-items\/edit\/\d+$/,
          ],
        },
        {
          parent: "/customers",
          paths: ["/customer/rate"],
        },
        {
          parent: "/customer-address-book",
          paths: [
            "/customer-address-book",
            "/customer-address-book/add",
            /^\/customer-address-book\/edit\/\d+$/,
          ],
        },
        // {
        //   parent: "/customer-markup",
        //   paths: [
        //     "/customer-markup",
        //     /^\/customer-markup\/edit\/\d+$/,
        //     "/customer-markup/add",
        //   ],
        // },
      ],
    },
    agentManagement: {
      links: [
        {
          to: "/agent-list",
          label: "My Agent List",
          roles: isUserOfOmniMove
            ? [
              "superadmin",
              "admin",
              "manager",
              "employee",
              "customersuperadmin",
            ]
            : ["admin", "manager", "employee", "customersuperadmin"],
        },
        {
          to: "/agent-rates",
          label: "Agent Rates",
          roles: isUserOfOmniMove
            ? []
            : ["admin", "manager", "customersuperadmin"],
        },
        {
          to: "/master-agent-list",
          label: "Master Agent List",
          roles: isUserOfOmniMove
            ? [
              "superadmin",
              "admin",
              "manager",
              "employee",
              "customersuperadmin",
            ]
            : ["admin", "manager", "employee", "customersuperadmin"] ||
            (customerLevel === "second" && []) ||
            (customerLevel === "third" && ["customer", "employee"]),
        },
        {
          to: "/agent-request",
          label: "Agent Requests",
          roles: isUserOfOmniMove
            ? ["superadmin", "admin", "manager", "customersuperadmin"]
            : customerLevel === "first"
              ? ["admin", "manager", "customersuperadmin"]
              : [],
        },
      ],
      paths: [
        {
          parent: "/agent-list",
          paths: [
            "/agent-list",
            "/agent-list/add",
            /^\/agent-list\/edit\/\d+$/,
            /^\/agent-list\/\d+$/,
          ],
        },
        {
          parent: "/agent-rates",
          paths: [
            "/agent-rates",
            /^\/agent-list\/edit\/\d+$/,
            /^\/agent-list\/\d+$/,
          ],
          roles: isUserOfOmniMove
            ? ["admin", "manager", "employee", "customersuperadmin"]
            : ["admin", "manager", "employee", "customersuperadmin"],
        },
        {
          parent: "/master-agent-list",
          paths: ["/master-agent-list", /^\/master-agent-list\/\d+$/],
        },
        {
          parent: "/agent-request",
          paths: [
            "/agent-request",
            "/agent-request/add",
            /^\/agent-request\/edit\/[^/]+$/,
          ],
        },
        {
          parent: "/agent-additional-charges",
          paths: ["/agent-additional-charges"],
        },
        {
          parent: "/agent-hubs",
          paths: [
            "/agent-hubs",
            "/agent-hubs/add",
            /^\/agent-hubs\/edit\/\d+$/,
          ],
        },
        {
          parent: "/agent-doc-list",
          paths: [
            "/agent-doc-list",
            "/agent-doc/add",
            /^\/agent-doc\/edit\/\d+$/,
          ],
        },
      ],
    },
    repManagement: {
      links: [
        {
          to: "/rep-list",
          label: "My Rep List",
          roles:
            !isUserOfOmniMove && customerLevel === "first"
              ? ["admin", "manager", "customersuperadmin"]
              : [],
        },
      ],
      paths: [
        {
          parent: "/rep-list",
          paths: [
            "/rep-list",
            "/rep-list/add",
            /^\/rep-list\/edit\/[^/]+$/,
          ],
        },
      ],
    },
    agentRate: {
      links: [
        {
          to: "/agent-mile-zone",
          label: "Agent Mile Zone",
          roles: ["superadmin", "admin", "manager", "customersuperadmin"],
        },
        {
          to: "/agent-weight-zone",
          label: "Agent Weight Zone",
          roles: ["superadmin", "admin", "manager", "customersuperadmin"],
        },
        {
          to: "/agent-rates",
          label: "Agent Rates",
          roles: ["superadmin", "admin", "manager", "customersuperadmin"],
        },
        {
          to: "/fsc-agent-list",
          label: "FSC Agent List",
          roles: ["superadmin", "admin", "manager", "customersuperadmin"],
        },
      ],
      paths: [
        {
          parent: "/agent-rates",
          paths: [
            "/agent-rates",
            "/agent-rates/add",
            /^\/agent-rates\/edit\/\d+$/,
          ],
        },
        {
          parent: "/agent-mile-zone",
          paths: [
            "/agent-mile-zone",
            "/agent-mile-zone/add",
            /^\/agent-mile-zone\/edit\/\d+$/,
          ],
        },
        {
          parent: "/agent-weight-zone",
          paths: [
            "/agent-weight-zone",
            "/agent-weight-zone/add",
            /^\/agent-weight-zone\/edit\/\d+$/,
          ],
        },
        {
          parent: "/fsc-agent-list",
          paths: [
            "/fsc-agent-list",
            "/fsc-agent-list/add",
            /^\/fsc-agent-list\/edit\/\d+$/,
          ],
        },
      ],
    },
    orderManagement: {
      links: [
        {
          to: "/active-order",
          label: "Active Orders",
          roles: isUserOfOmniMove
            ? ["agent"]
            : [
              "admin",
              "customer",
              "manager",
              "employee",
              "agent",
              "customersuperadmin",
              SALES_REP_ROLE,
            ],
        },
        {
          to: "/delivered-order",
          label: "Delivered Orders",
          roles: isUserOfOmniMove
            ? ["agent"]
            : [
              "admin",
              "customer",
              "manager",
              "employee",
              "agent",
              "customersuperadmin",
              SALES_REP_ROLE,
            ],
        },
        {
          to: "/finalized-order",
          label: "Finalized Orders",
          roles: isUserOfOmniMove
            ? ["agent"]
            : [
              "admin",
              "customer",
              "manager",
              "employee",
              "agent",
              "customersuperadmin",
              SALES_REP_ROLE,
            ],
        },
        {
          to: "/cancelled-order",
          label: "Cancelled Orders",
          roles: isUserOfOmniMove
            ? ["agent"]
            : [
              "admin",
              "customer",
              "manager",
              "employee",
              "agent",
              "customersuperadmin",
              SALES_REP_ROLE,
            ],
        },
        {
          to: "/invoiced-order",
          label: "Invoiced Orders",
          roles: isUserOfOmniMove
            ? ["agent"]
            : [
              "admin",
              "customer",
              "manager",
              "employee",
              "agent",
              "customersuperadmin",
              SALES_REP_ROLE,
            ],
        },
        {
          to: "/all-orders",
          label: "All Orders",
          roles: isUserOfOmniMove
            ? ["agent"]
            : [
              "admin",
              "customer",
              "manager",
              "employee",
              "agent",
              "customersuperadmin",
              SALES_REP_ROLE,
            ],
        },
        {
          to: "/saved-quotes",
          label: "Saved Quotes",
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first" || customerLevel === "second"
              ? ["admin", "manager", "employee", "customer", "customersuperadmin"]
              : [],
        },
        {
          to: "/dispatch-board-list",
          label: "Dispatch Board",
          roles: isUserOfOmniMove
            ? []
            : [
              "admin",
              "customer",
              "manager",
              "employee",
              "customersuperadmin",
              SALES_REP_ROLE,
            ],
        },
        {
          to: "/billed-order-history",
          label: "Billed Order History",
          roles: isUserOfOmniMove
            ? [
              "superadmin",
              "admin",
              "manager",
              "employee",
              "customersuperadmin",
            ]
            : [],
        },
        // {
        //   to: "/bulk-quote-import",
        //   label: "Bulk Quote Import",
        //   roles: isUserOfOmniMove
        //     ? []
        //     : ["superadmin", "admin", "manager", "employee"],
        // },
        {
          to: "/bulk-quote-listing",
          label: "Bulk Quote Listing",
          roles:
            customerLevel === "first"
              ? [
                "customersuperadmin",
                "superadmin",
                "admin",
                "manager",
                "employee",
              ]
              : [],
        },
        // {
        //   to: "/bulk-order-import",
        //   label: "Bulk Order Import",
        //   roles: isUserOfOmniMove
        //     ? []
        //     : ["superadmin", "admin", "manager", "employee"],
        // },
        {
          to: "/bulk-order-listing",
          label: "Bulk Order Listing",
          roles:
            customerLevel === "first"
              ? [
                "customersuperadmin",
                "superadmin",
                "admin",
                "manager",
                "employee",
              ]
              : [],
        },
      ],
      paths: [
        {
          parent: "/all-orders",
          paths: [
            "/customer-order",
            "/quick-quote",
            "/all-orders",
            /^\/all-orders\/edit\/\d+$/,
          ],
        },
        // {
        //   parent: "/all-orders",
        //   paths: [
        //     "/quick-quote",
        //     "/all-orders",
        //     /^\/all-orders\/edit\/\d+$/,
        //   ],
        // },
        {
          parent: "/dispatch-board-list",
          paths: ["/dispatch-board-list"],
        },
        {
          parent: "/billed-order-history",
          paths: [
            "/billed-order-history",
            /^\/billed-order-history\/view\/\d+$/,
          ],
        },
        {
          parent: "/saved-quotes",
          paths: ["/saved-quotes"],
        },
        {
          parent: "/active-order",
          paths: ["/active-order"],
        },
        {
          parent: "/invoiced-order",
          paths: ["/invoiced-order"],
        },
        {
          parent: "/finalized-order",
          paths: ["/finalized-order"],
        },
        {
          parent: "/cancelled-order",
          paths: ["/cancelled-order"],
        },
        {
          parent: "/delivered-order",
          paths: ["/delivered-order"],
        },
        {
          parent: "/bulk-quote-import",
          paths: ["/bulk-quote-import"],
        },
        {
          parent: "/bulk-quote-listing",
          paths: ["/bulk-quote-listing"],
        },
        {
          parent: "/bulk-order-import",
          paths: ["/bulk-order-import"],
        },
        {
          parent: "/bulk-order-listing",
          paths: ["/bulk-order-listing"],
        },
      ],
    },
    apiManagement: {
      links: [
        {
          to: "/Xcelarator-api",
          label: "Xcelarator API",
          roles: ["*"],
        },
      ],
      paths: [
        {
          parent: "/Xcelarator-api",
          paths: [
            "/Xcelarator-api",
            "/Xcelarator-api/add",
            /^\/Xcelarator-api\/edit\/\d+$/,
            /^\/Xcelarator-api\/agent\/\d+$/,
          ],
        },
      ],
    },
    accountManagement: {
      links: [
        {
          to: "/group-invoice-list",
          label: "Customer Invoice Groups",
          roles: [
            "superadmin",
            "admin",
            "manager",
            "employee",
            "customersuperadmin",
          ],
        },
        {
          to: "/invoice-list",
          label: "Order Invoice",
          roles: [
            "superadmin",
            "admin",
            "manager",
            "employee",
            "customersuperadmin",
          ],
        },
        {
          to: "/finalized-orders",
          label: "Finalized Orders",
          roles: [
            "superadmin",
            "admin",
            "manager",
            "employee",
            "customersuperadmin",
          ],
        },
      ],
      paths: [
        {
          parent: "/group-invoice-list",
          paths: [
            "/group-invoice-list",
            "/group-invoice-list/add",
            /^\/group-invoice-list\/edit\/\d+$/,
          ],
        },
        {
          parent: "/invoice-list",
          paths: [
            "/invoice-list",
            "/invoice-list/add",
            /^\/invoice-list\/edit\/\d+$/,
          ],
        },
        {
          parent: "/finalized-orders",
          paths: ["/finalized-orders"],
        },
        {
          parent: "/cancelled-order",
          paths: ["/cancelled-order"],
        },
        {
          parent: "/delivered-order",
          paths: ["/delivered-order"],
        },
      ],
    },
    agentApi: {
      links: [
        {
          to: "/agent-api",
          label: "Agent API",
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first"
              ? ["admin", "manager", "customersuperadmin"]
              : [],
        },
      ],
      paths: [
        {
          parent: "/agent-api",
          paths: ["/agent-api", "/agent-api/add"],
        },
      ],
    },
    report: {
      links: [
        {
          to: "/create-report",
          label: "Create Report",
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first"
              ? ["admin", "manager", "employee", "customersuperadmin"]
              : [],
        },
        {
          to: "/saved-reports",
          label: "Saved Reports",
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first"
              ? ["admin", "manager", "employee", "customersuperadmin"]
              : [],
        },
      ],
      paths: [
        {
          parent: "/create-report",
          paths: ["/create-report"],
        },
        {
          parent: "/saved-reports",
          paths: ["/saved-reports"],
        },
      ],
    },
    setting: {
      links: [
        {
          to: "/settings-additional-charges",
          label: "Additional Charges",
          roles: ["superadmin", "admin", "manager", "customersuperadmin"],
        },
      ],
      paths: [
        {
          parent: "/settings-additional-charges",
          paths: [
            "/settings-additional-charges",
            "/settings-additional-charges/add",
            /^\/settings-additional-charges\/edit\/\d+$/,
          ],
        },
      ],
    },
    communications: {
      links: [
        {
          to: "/communications/unassigned",
          label: "Unassigned",
          // Everyone at a first-level customer who works tickets.
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first"
              ? ["admin", "manager", "employee", "customersuperadmin"]
              : [],
        },
        {
          to: "/communications/assigned-to-me",
          label: "Assigned To Me",
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first"
              ? ["admin", "manager", "employee", "customersuperadmin"]
              : [],
        },
        // The POD Requests and Spot Quotes listings, each its own entry (sprint63 Spot Quotes listing Decision D1)
        { to: "/communications/pod-requests", label: "POD Requests", roles: communicationsRoles },
        { to: "/communications/spot-quotes", label: "Spot Quotes", roles: communicationsRoles },
        {
          // Groups (KAN-1362): Exceptions while the Exception group exists (KAN-1347), the groups picked under View
          // Another Group (none by default), and View Another Group, which opens the pop-up
          id: "groups",
          label: "Groups",
          children: [
            ...(exceptionGroup
              ? [{ to: "/communications/exceptions", label: "Exceptions", roles: communicationsRoles }]
              : []),
            ...viewedGroups.map((group) => ({ to: groupRoute(group.id), label: group.name, roles: communicationsRoles })),
            {
              to: "#view-another-group",
              label: "View Another Group",
              roles: communicationsRoles,
              onSelect: () => setIsViewGroupsOpen(true),
            },
          ],
        },
        {
          // A nested group, as System Configuration › Helpdesk; each agent's settings (Spot Quotes listing D2)
          id: "claudeAgents",
          label: "Claude Agents",
          children: [
            {
              to: "/communications/claude-agents/pod-requests",
              label: "POD Requests",
              roles: isUserOfOmniMove
                ? []
                : customerLevel === "first"
                  ? ["admin", "manager", "employee", "customersuperadmin"]
                  : [],
            },
            {
              to: "/communications/claude-agents/spot-quote",
              label: "Spot Quote",
              roles: isUserOfOmniMove
                ? []
                : customerLevel === "first"
                  ? ["admin", "manager", "employee", "customersuperadmin"]
                  : [],
            },
            // Status Update and Reasons come back when those agents are built
          ],
        },
      ],
      paths: [
        {
          parent: "/communications/unassigned",
          paths: ["/communications/unassigned", /^\/communications\/unassigned\/[^/]+$/],
        },
        {
          parent: "/communications/assigned-to-me",
          paths: ["/communications/assigned-to-me", /^\/communications\/assigned-to-me\/[^/]+$/],
        },
        {
          parent: "/communications/exceptions",
          // Its Open and Resolved tabs (KAN-1325) and a ticket opened from either
          paths: ["/communications/exceptions", /^\/communications\/exceptions\/[^/]+$/, /^\/communications\/exceptions\/resolved\/[^/]+$/],
        },
        // A viewed group: its Open and Resolved tabs and a ticket opened from either (KAN-1362)
        ...viewedGroups.map((group) => ({ parent: groupRoute(group.id), paths: [groupRoute(group.id)] })),
        {
          // The listing and a POD request
          parent: "/communications/pod-requests",
          paths: ["/communications/pod-requests", /^\/communications\/pod-requests\/[^/]+$/],
        },
        {
          // The listing and a round
          parent: "/communications/spot-quotes",
          paths: ["/communications/spot-quotes", /^\/communications\/spot-quotes\/[^/]+$/],
        },
        {
          // POD settings
          parent: "/communications/claude-agents/pod-requests",
          paths: ["/communications/claude-agents/pod-requests"],
        },
        {
          parent: "/communications/claude-agents/spot-quote",
          paths: ["/communications/claude-agents/spot-quote"],
        },
      ],
    },
    systemConfiguration: {
      links: [
        {
          to: "/system-configuration/services",
          label: "Services",
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first"
              ? ["admin", "manager", "employee", "customersuperadmin"]
              : [],
        },
        {
          to: "/system-configuration/onward-service",
          label: "Services",
          roles: isUserOfOmniMove
            ? ["superadmin"]
            : []
        },
        {
          to: "/system-configuration/order-status",
          label: "Order Status",
          roles: isUserOfOmniMove
            ? ["superadmin", "admin"]
            : []
        },
        {
          to: "/system-configuration/vehicle",
          label: "Vehicle Type",
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first"
              ? ["admin", "manager", "employee", "customersuperadmin"]
              : [],
        },
        {
          to: "/system-configuration/package-type",
          label: "Package Type",
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first"
              ? ["admin", "manager", "employee", "customersuperadmin"]
              : [],
        },
        {
          to: "/system-configuration/email-template",
          label: "Email Template",
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first"
              ? ["admin", "customersuperadmin"]
              : [],
        },
        {
          to: "/system-configuration/scac",
          label: "SCAC",
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first"
              ? ["admin", "manager", "employee", "customersuperadmin"]
              : [],
        },
        {
          to: "/system-configuration/emailConfig",
          label: "Email Configuration",
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first"
              ? ["admin", "customersuperadmin"]
              : [],
        },
        {
          to: "/system-configuration/region",
          label: "Region",
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first"
              ? ["admin", "customersuperadmin"]
              : [],
        },
        {
          to: "/system-configuration/fsc-location",
          label: "FSC Location",
          roles: isUserOfOmniMove
            ? []
            : customerLevel === "first"
              ? ["admin", "customersuperadmin"]
              : [],
        },
        {
          to: "/system-configuration/zip-code-list",
          label: "Zipcodes",
          roles: isUserOfOmniMove ? ["superadmin", "admin"] : [],
        },
        {
          id: "helpdesk",
          label: "Helpdesk",
          children: [
            {
              to: "/system-configuration/groups-assignment",
              label: "Groups & Assignment",
              roles: isUserOfOmniMove
                ? []
                : customerLevel === "first"
                  ? ["admin", "customersuperadmin"]
                  : [],
            },
            // Hidden for now (user, 2026-10-19); page and route code are kept.
            // {
            //   to: "/system-configuration/sla-policies",
            //   label: "SLA Policies",
            //   roles: isUserOfOmniMove
            //     ? []
            //     : customerLevel === "first"
            //       ? ["admin", "customersuperadmin"]
            //       : [],
            // },
            {
              to: "/system-configuration/business-hours",
              label: "Business Hours",
              roles: isUserOfOmniMove
                ? []
                : customerLevel === "first"
                  ? ["admin", "customersuperadmin"]
                  : [],
            },
            {
              to: "/system-configuration/mailboxes",
              label: "Mailboxes",
              roles: isUserOfOmniMove
                ? []
                : customerLevel === "first"
                  ? ["admin", "customersuperadmin"]
                  : [],
            },
            {
              to: "/system-configuration/signatures-sending",
              label: "Signatures & Sending",
              roles: isUserOfOmniMove
                ? []
                : customerLevel === "first"
                  ? ["admin", "customersuperadmin"]
                  : [],
            },
            {
              to: "/system-configuration/notifications",
              label: "Notifications",
              roles: isUserOfOmniMove
                ? []
                : customerLevel === "first"
                  ? ["admin", "customersuperadmin"]
                  : [],
            },
            {
              to: "/system-configuration/ticket-fields-statuses",
              label: "Ticket Fields & Statuses",
              roles: isUserOfOmniMove
                ? []
                : customerLevel === "first"
                  ? ["admin", "customersuperadmin"]
                  : [],
            },
            // Hidden for now (user, 2026-10-19); page and route code are kept.
            // {
            //   to: "/system-configuration/tags",
            //   label: "Tags",
            //   roles: isUserOfOmniMove
            //     ? []
            //     : customerLevel === "first"
            //       ? ["admin", "customersuperadmin"]
            //       : [],
            // },
            {
              to: "/system-configuration/claude-agent-settings",
              label: "Claude Agent Settings",
              roles: isUserOfOmniMove
                ? []
                : customerLevel === "first"
                  ? ["admin", "customersuperadmin"]
                  : [],
            },
          ],
        },
      ],
      paths: [
        {
          parent: "/system-configuration/services",
          paths: ["/system-configuration/services"],
        },
        {
          parent: "/system-configuration/package-type",
          paths: ["/system-configuration/package-type"],
        },
        {
          parent: "/system-configuration/vehicle",
          paths: ["/system-configuration/vehicle"],
        },
        {
          parent: "/system-configuration/email-template",
          paths: [
            "/system-configuration/email-template",
            "/system-configuration/add-email-template",
            "/system-configuration/edit-email-template",
          ],
        },
        {
          parent: "/system-configuration/scac",
          paths: ["/system-configuration/scac"],
        },
        {
          parent: "/system-configuration/emailConfig",
          paths: ["/system-configuration/emailConfig"],
        },
        {
          parent: "/system-configuration/region",
          paths: ["/system-configuration/region"],
        },
        {
          parent: "/system-configuration/fsc-location",
          paths: ["/system-configuration/fsc-location"],
        },
        {
          parent: "/system-configuration/order-status",
          paths: ["/system-configuration/order-status"],
        },
        {
          parent: "/system-configuration/onward-service",
          paths: ["/system-configuration/onward-service"],
        },
        {
          parent: "/system-configuration/zip-code-list",
          paths: ["/system-configuration/zip-code-list"],
        },
        {
          parent: "/system-configuration/mailboxes",
          paths: ["/system-configuration/mailboxes"],
        },
        {
          parent: "/system-configuration/groups-assignment",
          paths: ["/system-configuration/groups-assignment"],
        },
        {
          parent: "/system-configuration/sla-policies",
          paths: ["/system-configuration/sla-policies"],
        },
        {
          parent: "/system-configuration/business-hours",
          paths: ["/system-configuration/business-hours"],
        },
        {
          parent: "/system-configuration/signatures-sending",
          paths: ["/system-configuration/signatures-sending"],
        },
        {
          parent: "/system-configuration/notifications",
          paths: ["/system-configuration/notifications"],
        },
        {
          parent: "/system-configuration/ticket-fields-statuses",
          paths: ["/system-configuration/ticket-fields-statuses"],
        },
        {
          parent: "/system-configuration/claude-agent-settings",
          paths: ["/system-configuration/claude-agent-settings"],
        },
        {
          parent: "/system-configuration/tags",
          paths: ["/system-configuration/tags"],
        },
        {
          parent: "/system-configuration/canned-responses",
          paths: ["/system-configuration/canned-responses"],
        },
      ],
    },
  };

  /**
   * Checks if the current location matches any path associated with a specific parent route.
   * This is used to determine if a link in the sidebar should be highlighted as active.
   *
   * @param parent - The parent route to check against the current location.
   * @returns True if the current path matches the parent route or its subpaths.
   */
  const isActiveLink = (parent: string): boolean => {
    return Object.values(routeConfig).some((config) =>
      config.paths.some(
        (p) =>
          p.parent === parent &&
          p.paths.some((path) =>
            typeof path === "string"
              ? location.pathname === path ||
              location.pathname.startsWith(path + "/")
              : path.test(location.pathname)
          )
      )
    );
  };

  /**
   * Checks if the specific path exactly matches the current location.
   *
   * @param path - The specific path to check.
   * @returns True if the current path matches the provided path.
   */
  const isSpecificLinkActive = (path: string): boolean => {
    return location.pathname === path;
  };

  /**
   * Closes the currently open dropdown menu.
   */
  const handleCloseDropdown = () => {
    setOpenDropdown(null);
  };

  /**
   * Toggles the state of a dropdown menu between open and closed.
   *
   * @param dropdown - The name of the dropdown menu to toggle.
   */
  const handleDropdownClick = (dropdown: string) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  /**
   * Handles navigation when a link is clicked, taking into unsaved form changes.
   *
   * If there are unsaved form changes, the user is prompted before navigating away.
   * Otherwise, the navigation proceeds as normal.
   *
   * @param path - The destination path to navigate to.
   * @param e - The click event that triggered the navigation.
   */
  const handleLinkNavigation = (
    path: string,
    e: React.MouseEvent<HTMLElement>
  ) => {
    sessionStorage.removeItem('ltlIntegrationEdit')
    dispatch(setLocationData(null));
    dispatch(setActiveTab(null));
    dispatch(setGetPath(path));
    if (isFormChanged) {
      e.preventDefault();
      handleNavigation(); // showing a confirmation dialog
    } else {
      document.body.classList.remove("sidebar-hidden"); // ✅ collapse sidebar on mobile
      navigate(path);
    }
  };

  /**
   * Determines the dropdown menu that should be open based on the current path.
   * Sets the state of the `openDropdown` to the corresponding dropdown.
   */
  useEffect(() => {
    const determineDropdown = () => {
      for (const [dropdown, config] of Object.entries(routeConfig)) {
        if (
          config.paths.some((p) =>
            p.paths.some((path) =>
              typeof path === "string"
                ? location.pathname === path ||
                location.pathname.startsWith(path + "/")
                : path.test(location.pathname)
            )
          )
        ) {
          return dropdown;
        }
      }
      return null;
    };

    setOpenDropdown(determineDropdown());
  }, [location.pathname]);

  return (
    <>
      <div className="layoutSidemenu">
        <div className="layoutSidemenu__logo">
          <img
            onClick={(e) => {
              handleLinkNavigation("/", e);
            }}
            src={logoUrl}
            alt="Logo | Dickerson Transportation Solutions"
          />
          <button className="btnCollapseDesktop" id="sidebarCollapseDesktop" onClick={() => document.body.classList.toggle("sidebar-hidden-desktop")}>
            <svg width="19" height="17" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 14.45V16.45H1V14.45H19ZM4.95 0V9.9L0 4.95L4.95 0ZM19 7.45V9.45H10V7.45H19ZM19 0.45V2.45H10V0.45H19Z" fill="black" />
            </svg>
            <svg className="btnCollapseDesktop__closeIcon" width="19" height="17" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 14.45V16.45H0V14.45H18ZM14.05 0L19 4.95L14.05 9.9V0ZM9 7.45V9.45H0V7.45H9ZM9 0.45V2.45H0V0.45H9Z" fill="black" />
            </svg>
          </button>
        </div>
        <div className="layoutSidemenu__fullHeight">
          <ul className="layoutSidemenu__list">
            <SidebarPlainItem
              className={`layoutSidemenu__list--item ${isSpecificLinkActive("/") ? "layoutSidemenu__selected" : ""}`}
              onClick={handleCloseDropdown}
              titleLabel="Dashboard"
              to="/"
              handleLinkNavigation={handleLinkNavigation}
              isActive={isSpecificLinkActive("/")}
            >
              <Link
                className="layoutSidemenu__list--link"
                onClick={(e) => { handleLinkNavigation("/", e); }}
                to="/"
              >
                <span className="icon">
                  <DashboardSidebarIcon />
                </span>
                <span>Dashboard</span>
              </Link>
            </SidebarPlainItem>
            {userRole !== "agent" && userRole !== SALES_REP_ROLE && (
              <DropdownMenu
                title="User Management"
                icon={<DoubleUserIcon />}
                links={routeConfig.userManagement.links}
                isActiveLink={(path) => isActiveLink(path)}
                isOpen={openDropdown === "userManagement"}
                onDropdownClick={() => handleDropdownClick("userManagement")}
                userIconClass={true}
                handleLinkNavigation={handleLinkNavigation}
              />
            )}
            <SidebarPlainItem
              className={`layoutSidemenu__list--item ${isSpecificLinkActive(
                location.pathname === "/user/profile" ? "/user/profile" : "/email-notification-permissions"
              ) ? "layoutSidemenu__selected" : ""}`}
              onClick={handleCloseDropdown}
              titleLabel="Profile"
              to="/user/profile"
              handleLinkNavigation={handleLinkNavigation}
              isActive={location.pathname === "/user/profile" || location.pathname === "/email-notification-permissions"}
            >
              <Link
                className="layoutSidemenu__list--link"
                onClick={(e) => { handleLinkNavigation("/user/profile", e); }}
                to="/user/profile"
              >
                <span className="icon">
                  <ProfileSidebarIcon />
                </span>
                <span>Profile</span>
              </Link>
            </SidebarPlainItem>
            <SidebarPlainItem
              className={`layoutSidemenu__list--item ${isSpecificLinkActive("/change-password") ? "layoutSidemenu__selected" : ""}`}
              onClick={handleCloseDropdown}
              titleLabel="Change Password"
              to="/change-password"
              handleLinkNavigation={handleLinkNavigation}
              isActive={isSpecificLinkActive("/change-password")}
            >
              <Link
                className="layoutSidemenu__list--link"
                onClick={(e) => { handleLinkNavigation("/change-password", e); }}
                to="/change-password"
              >
                <span className="icon">
                  <ChangePasswordSidebarIcon />
                </span>
                <span>Change Password</span>
              </Link>
            </SidebarPlainItem>
            {userRole !== "employee" &&
              userRole !== "agent" &&
              userRole !== "customer" &&
              userRole !== SALES_REP_ROLE &&
              customerLevel !== "second" &&
              customerLevel !== "third" && (
                <DropdownMenu
                  title="Agent Management"
                  icon={<AgentManagementIcon />}
                  links={routeConfig.agentManagement.links}
                  isActiveLink={(path) => isActiveLink(path)}
                  isOpen={openDropdown === "agentManagement"}
                  onDropdownClick={() => handleDropdownClick("agentManagement")}
                  userIconClass={true}
                  userRole={userRole || ""}
                  handleLinkNavigation={handleLinkNavigation}
                />
              )}
            {!isUserOfOmniMove &&
              customerLevel === "first" &&
              (userRole === "admin" ||
                userRole === "manager" ||
                userRole === "customersuperadmin") && (
                <DropdownMenu
                  title="Rep Management"
                  icon={<RepManagementIcon />}
                  links={routeConfig.repManagement.links}
                  isActiveLink={(path) => isActiveLink(path)}
                  isOpen={openDropdown === "repManagement"}
                  onDropdownClick={() => handleDropdownClick("repManagement")}
                  userIconClass={true}
                  userRole={userRole || ""}
                  handleLinkNavigation={handleLinkNavigation}
                />
              )}
            {userRole !== "employee" &&
              userRole !== "agent" &&
              userRole !== "customer" &&
              customerLevel !== "third" && (
                <DropdownMenu
                  title="Customer Management"
                  icon={<CustomerIcon />}
                  links={routeConfig.customerManagement.links}
                  isActiveLink={(path) => isActiveLink(path)}
                  isOpen={openDropdown === "customerManagement"}
                  onDropdownClick={() =>
                    handleDropdownClick("customerManagement")
                  }
                  userIconClass={false}
                  userRole={userRole || ""}
                  handleLinkNavigation={handleLinkNavigation}
                />
              )}
            <DropdownMenu
              title="Order Management"
              icon={<OrderManagementIcon />}
              links={
                userRole === "agent"
                  ? routeConfig.orderManagement.links.filter(
                    (link) =>
                      link.label !== "Finalized Orders" &&
                      link.label !== "Invoiced Orders"
                  )
                  : routeConfig.orderManagement.links
              }
              isActiveLink={(path) => isActiveLink(path)}
              isOpen={openDropdown === "orderManagement"}
              onDropdownClick={() => handleDropdownClick("orderManagement")}
              userIconClass={false}
              userRole={userRole || ""}
              handleLinkNavigation={handleLinkNavigation}
            />
            {!isUserOfOmniMove &&
              customerLevel === "first" &&
              userRole !== "agent" &&
              userRole !== SALES_REP_ROLE && (
                <DropdownMenu
                  title="Communications"
                  icon={<CommunicationsSidebarIcon />}
                  links={routeConfig.communications.links}
                  isActiveLink={(path) => isActiveLink(path)}
                  isOpen={openDropdown === "communications"}
                  onDropdownClick={() => handleDropdownClick("communications")}
                  userIconClass={false}
                  userRole={userRole || ""}
                  handleLinkNavigation={handleLinkNavigation}
                />
              )}
            {/* <DropdownMenu
              title="Api Management"
              icon={<ApiSidebarIcon />}
              links={routeConfig.apiManagement.links}
              isActiveLink={(path) => isActiveLink(path)}
              isOpen={openDropdown === "apiManagement"}
              onDropdownClick={() => handleDropdownClick("apiManagement")}
              userIconClass={true}
              userRole={userRole || ""}
              handleLinkNavigation={handleLinkNavigation}
            /> */}
            {customerLevel === "first" &&
              userRole !== "agent" &&
              userRole !== SALES_REP_ROLE && (
                <DropdownMenu
                  title="Accounting"
                icon={<AccountingIcon />}
                links={routeConfig.accountManagement.links}
                isActiveLink={(path) => isActiveLink(path)}
                isOpen={openDropdown === "accountManagement"}
                onDropdownClick={() => handleDropdownClick("accountManagement")}
                userIconClass={true}
                userRole={userRole || ""}
                handleLinkNavigation={handleLinkNavigation}
              />
            )}
            {/* {customerLevel === "first" && (
              <DropdownMenu
                title="Settings"
                icon={<SettingSidebarIcon />}
                links={routeConfig.setting.links}
                isActiveLink={(path) => isActiveLink(path)}
                isOpen={openDropdown === "setting"}
                onDropdownClick={() => handleDropdownClick("setting")}
                userIconClass={true}
                userRole={userRole || ""}
                handleLinkNavigation={handleLinkNavigation}
              />
            )} */}
            {customerLevel === "first" &&
              userRole !== "agent" &&
              userRole !== SALES_REP_ROLE && (
                <DropdownMenu
                  title="Report"
                icon={<AgentManagementIcon />}
                links={routeConfig.report.links}
                isActiveLink={(path) => isActiveLink(path)}
                isOpen={openDropdown === "report"}
                onDropdownClick={() => handleDropdownClick("report")}
                userIconClass={true}
                userRole={userRole || ""}
                handleLinkNavigation={handleLinkNavigation}
              />
            )}
            {customerLevel === "first" &&
              userRole !== "agent" &&
              userRole !== "employee" &&
              userRole !== SALES_REP_ROLE && (
                <DropdownMenu
                  title="API Management"
                  icon={<ApiSidebarIcon />}
                  links={routeConfig.agentApi.links}
                  isActiveLink={(path) => isActiveLink(path)}
                  isOpen={openDropdown === "agentApi"}
                  onDropdownClick={() => handleDropdownClick("agentApi")}
                  userIconClass={true}
                  userRole={userRole || ""}
                  handleLinkNavigation={handleLinkNavigation}
                />
              )}
            {userRole !== SALES_REP_ROLE &&
              (
                (customerLevel === "first" && userRole !== "agent" && userRole !== "employee" && userRole !== "manager") ||
                (isUserOfOmniMove && userRole !== "agent" && userRole !== "employee" && userRole !== "manager" && userRole !== "admin")
              ) && (
                <DropdownMenu
                  title="System Configuration"
                  icon={<ServiceConfigIcon />}
                  links={routeConfig.systemConfiguration.links}
                  isActiveLink={(path) => isActiveLink(path)}
                  isOpen={openDropdown === "systemConfiguration"}
                  onDropdownClick={() =>
                    handleDropdownClick("systemConfiguration")
                  }
                  userIconClass={true}
                  userRole={userRole || ""}
                  handleLinkNavigation={handleLinkNavigation}
                />
              )}
            {!isUserOfOmniMove &&
              customerLevel === "first" &&
              userRole !== "agent" &&
              userRole !== SALES_REP_ROLE && (
                <SidebarPlainItem
                  className={`layoutSidemenu__list--item ${isSpecificLinkActive("/audit-log") ? "layoutSidemenu__selected" : ""}`}
                  onClick={handleCloseDropdown}
                  titleLabel="Audit Log"
                  to="/audit-log"
                  handleLinkNavigation={handleLinkNavigation}
                  isActive={isSpecificLinkActive("/audit-log")}
                >
                  <Link
                    className="layoutSidemenu__list--link"
                    onClick={(e) => { handleLinkNavigation("/audit-log", e); }}
                    to="/audit-log"
                  >
                    <span className="icon">
                      <AuditLogSidebarIcon />
                    </span>
                    <span>Audit Log</span>
                  </Link>
                </SidebarPlainItem>
              )}
          </ul>
        </div>
        <div className="layoutSidemenu__version">
          <h4 className="layoutSidemenu__version__title">
            Version {version || "1.0.0"}
          </h4>
        </div>
      </div>
      {isViewGroupsOpen && (
        <ViewAnotherGroupModal onClose={() => setIsViewGroupsOpen(false)} onSaved={setViewGroups} />
      )}
    </>
  );
};

export default SideBar;
